// ImportBackupWizard — fresh-install path that restores app state
// from a `vaultwisp-backup-v1` file the user produced via Settings →
// Export.
//
// Three internal steps (no URL persistence — refreshing starts over,
// which is the right behavior for this short flow):
//   1. File + backup password → decrypt → preview vault list
//   2. Master choice (use backup password OR set a different master)
//   3. Create OPFS vault + save imported config + (if different
//      master) rotate every synced S3 vault to the new master
//
// The "different master" path stamps a `pending_rotation` marker on
// every synced non-OPFS vault BEFORE the first `saveAppConfig` so
// that an aborted rotation lands the user on `MasterUnlockScreen`
// with the standard `PendingRotationBanner` retry surface — no new
// recovery UI needed.

import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { zxcvbn } from "@zxcvbn-ts/core";
import type { Remote } from "comlink";
import { useVault } from "../../hooks/useVault";
import { useMasterSession } from "../../stores/masterSession";
import { useSession } from "../../stores/session";
import { toPassphraseBytes, zeroize } from "../../lib/passphraseBytes";
import {
  buildImportedAppConfig,
  newOpfsVaultId,
} from "../../lib/importBackup";
import {
  retryPendingRotations,
  type RotationProgress,
  type WorkerSubset,
} from "../../lib/masterRotation";
import type { AppConfig, VaultWorkerApi } from "../../worker/api";
import { PassphraseInput } from "../primitives/PassphraseInput";
import { PassphraseStrength } from "../primitives/PassphraseStrength";
import { MasterRotationModal } from "./MasterRotationModal";

type WorkerProxy = Remote<VaultWorkerApi>;

type Step = "pick" | "master" | "creating";
type MasterMode = "use-backup" | "set-new";
const OPFS_SUBDIR = "";

/**
 * Decrypted backup state passed from `PickStep` to `CreateStep`.
 *
 * Note on `password`: stored as an immutable JS string. Strings in
 * V8 cannot be zeroized — we have no API to scrub the underlying
 * bytes — so the backup password persists in heap memory until the
 * wizard component unmounts (or the GC collects, whichever comes
 * later). We accept this for the wizard's short lifetime; a
 * future hardening pass could move to a `Uint8Array` variant if
 * needed.
 */
interface DecryptedBackup {
  readonly password: string;
  readonly config: AppConfig;
}

export function ImportBackupWizard() {
  const navigate = useNavigate();
  const worker = useVault();
  const setMaster = useMasterSession((s) => s.setMaster);
  const setSession = useSession((s) => s.setSession);

  const [step, setStep] = useState<Step>("pick");
  const [decrypted, setDecrypted] = useState<DecryptedBackup | null>(null);
  const [createError, setCreateError] = useState<string | null>(null);
  const [rotationProgress, setRotationProgress] =
    useState<RotationProgress | null>(null);
  const createRef = useRef(false);

  function reset(): void {
    setStep("pick");
    setDecrypted(null);
    setCreateError(null);
    setRotationProgress(null);
    createRef.current = false;
    // Drop any chosen master left over from a previous run so the
    // module-scoped stash doesn't keep a sensitive value alive
    // across "Start over" / re-mount cycles.
    createRefStash.master = null;
    createRefStash.mode = "use-backup";
  }

  if (step === "pick") {
    return (
      <PickStep
        onDecrypted={(d) => {
          setDecrypted(d);
          setStep("master");
        }}
      />
    );
  }

  if (step === "master" && decrypted !== null) {
    return (
      <MasterStep
        backup={decrypted.config}
        onBack={reset}
        onChosen={(mode, newMaster) => {
          // The pipeline runs as a side-effect in CreateStep; the
          // chosen master + mode are stashed module-scoped to avoid
          // putting the master in React state for any longer than
          // necessary.
          createRefStash.master = newMaster;
          createRefStash.mode = mode;
          setStep("creating");
        }}
      />
    );
  }

  if (step === "creating" && decrypted !== null) {
    return (
      <CreateStep
        decrypted={decrypted}
        chosenMaster={createRefStash.master ?? ""}
        mode={createRefStash.mode}
        createError={createError}
        rotationProgress={rotationProgress}
        worker={worker}
        setMaster={setMaster}
        setSession={setSession}
        navigate={navigate}
        onError={setCreateError}
        onRotationProgress={setRotationProgress}
        onStartOver={reset}
        startedRef={createRef}
      />
    );
  }

  return <Link to="/import">Start over</Link>;
}

// `createRefStash` is a module-scoped object holding the chosen
// master + mode between the master-step and the create-step. Using a
// stash avoids threading them through React state (which would
// require putting the master password in `useState` for longer than
// strictly necessary). Cleared by `reset()` AND by `runImport`'s
// `finally`, so the chosen master never outlives the import
// pipeline (regardless of success or failure). The module-scoped
// shape is intentional — the wizard remounts on every `/import`
// visit, so a per-component `useRef` would not preserve the value
// across the `master → creating` step transition.
const createRefStash: {
  master: string | null;
  mode: MasterMode;
} = { master: null, mode: "use-backup" };

/**
 * Translate a thrown error from `worker.openBackup` into copy
 * suitable for the user-facing error banner. Pre-AEAD failures
 * (truncation, magic, version, KDF params) keep their original
 * message because the wording is already user-actionable. AEAD
 * failures (rendered as "AEAD decryption failed …" by the Rust
 * crypto layer) collapse to a single friendly message — wrong
 * password and tampering must remain indistinguishable, so we
 * never surface either label in the UI.
 */
function friendlyOpenBackupError(err: unknown): string {
  const raw = err instanceof Error ? err.message : String(err);
  if (raw.toLowerCase().includes("aead")) {
    return "Wrong password or corrupted backup file.";
  }
  return raw;
}

// ————— Step 1: pick file + decrypt ——————————————————————————

interface PickStepProps {
  readonly onDecrypted: (d: DecryptedBackup) => void;
}

function PickStep({ onDecrypted }: PickStepProps) {
  const worker = useVault();
  const [file, setFile] = useState<File | null>(null);
  const [password, setPassword] = useState("");
  const [phase, setPhase] = useState<"idle" | "decrypting">("idle");
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    if (file === null || password.length === 0) return;
    setPhase("decrypting");
    setError(null);
    const passwordBytes = toPassphraseBytes(password);
    try {
      // The encrypted bytes are consumed by `openBackup` — we don't
      // hold on to them here, so a corrupted-or-evil file can't sit
      // in JS heap longer than this single decrypt call.
      const buf = new Uint8Array(await file.arrayBuffer());
      const cfg = await worker.openBackup(buf, passwordBytes);
      onDecrypted({ password, config: cfg });
    } catch (err) {
      setPhase("idle");
      setError(friendlyOpenBackupError(err));
    } finally {
      zeroize(passwordBytes);
    }
  }

  return (
    <div className="mx-auto min-h-[70vh] max-w-md px-5 py-8 pb-[calc(env(safe-area-inset-bottom)+24px)] md:px-6 md:py-12">
      <h1 className="mb-2 text-2xl font-medium tracking-tight">
        Import from backup
      </h1>
      <p className="mb-4 text-sm text-text-soft">
        Select the encrypted backup file and enter the password used when it
        was exported.
      </p>

      <form onSubmit={(e) => void onSubmit(e)}>
        <label
          htmlFor="import-file"
          className="mb-1 block text-[11px] uppercase tracking-[0.1em] text-text-dim"
        >
          Backup file
        </label>
        <input
          id="import-file"
          type="file"
          accept=".vwb,application/octet-stream"
          onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          className="mb-4 block w-full text-sm text-text-soft file:mr-3 file:rounded-md file:border-0 file:bg-accent file:px-3 file:py-2 file:text-sm file:font-medium file:text-white"
          aria-label="Backup file"
        />

        <label
          htmlFor="import-password"
          className="mb-1 block text-[11px] uppercase tracking-[0.1em] text-text-dim"
        >
          Backup password
        </label>
        <PassphraseInput
          id="import-password"
          autoCompleteMode="current-password"
          aria-label="Backup password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />

        {error !== null && (
          <div
            role="alert"
            className="mt-3 rounded-md border border-danger/40 bg-danger/10 p-3 text-[13px] text-danger"
          >
            <span className="font-mono">Could not open backup: {error}</span>
          </div>
        )}

        <div className="mt-6 flex flex-col-reverse gap-2 md:flex-row md:justify-between">
          <Link
            to="/setup/1"
            className="text-center text-[12px] text-text-dim underline hover:text-text"
          >
            ← Back to setup
          </Link>
          <button
            type="submit"
            disabled={
              file === null || password.length === 0 || phase === "decrypting"
            }
            className="rounded-md bg-accent px-4 py-3 text-sm font-medium text-white disabled:opacity-40 md:py-2"
          >
            {phase === "decrypting" ? "Decrypting…" : "Continue →"}
          </button>
        </div>
      </form>
    </div>
  );
}

// ————— Step 2: master choice ——————————————————————————————

interface MasterStepProps {
  readonly backup: AppConfig;
  readonly onBack: () => void;
  readonly onChosen: (mode: MasterMode, master: string) => void;
}

function MasterStep({ backup, onBack, onChosen }: MasterStepProps) {
  const [mode, setMode] = useState<MasterMode>("use-backup");
  const [newPass, setNewPass] = useState("");
  const [confirm, setConfirm] = useState("");

  const score = useMemo(
    () => (newPass.length > 0 ? zxcvbn(newPass).score : 0),
    [newPass],
  );
  const strong = score >= 3;
  const matches = newPass === confirm;

  // Vault-list preview surfaces *display names only* — credentials
  // remain hidden. Filter out OPFS entries since those are
  // device-local and replaced on import.
  const previewable = backup.vaults.filter((v) => v.adapter.type !== "opfs");

  const canContinue =
    mode === "use-backup" ||
    (newPass.length > 0 && confirm.length > 0 && strong && matches);

  function onSubmit(e: React.FormEvent): void {
    e.preventDefault();
    if (!canContinue) return;
    if (mode === "use-backup") {
      // The backup password flowed in via `decrypted.password` and is
      // re-used by CreateStep — pass an empty string here as a
      // sentinel; CreateStep ignores `chosenMaster` when mode is
      // "use-backup".
      onChosen("use-backup", "");
    } else {
      onChosen("set-new", newPass);
    }
  }

  return (
    <div className="mx-auto min-h-[70vh] max-w-md px-5 py-8 pb-[calc(env(safe-area-inset-bottom)+24px)] md:px-6 md:py-12">
      <h1 className="mb-2 text-2xl font-medium tracking-tight">
        Choose your master password
      </h1>

      <div className="mb-5 rounded-md border border-border bg-bg-raised p-3 text-[12px] text-text-soft">
        <div className="mb-1 font-medium text-text">
          Vaults found in backup: {previewable.length}
        </div>
        {previewable.length > 0 && (
          <ul className="ml-4 list-disc">
            {previewable.map((v) => (
              <li key={v.id}>
                {v.display_name}{" "}
                <span className="text-text-dim">
                  ({v.sync_state === "synced" ? "synced" : "unsynced"})
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      <form onSubmit={onSubmit} className="space-y-4">
        <fieldset className="space-y-3">
          <legend className="sr-only">Master-password choice</legend>

          <label className="flex cursor-pointer items-start gap-3 rounded-md border border-border p-3 text-[13px]">
            <input
              type="radio"
              name="master-mode"
              value="use-backup"
              checked={mode === "use-backup"}
              onChange={() => setMode("use-backup")}
              aria-label="Use the backup password as my master"
              className="mt-0.5"
            />
            <div>
              <div className="font-medium text-text">
                Use the backup password as my master
              </div>
              <div className="text-text-soft">
                Fast path — no per-vault rotation needed. Synced vaults keep
                using the same password as before.
              </div>
            </div>
          </label>

          <label className="flex cursor-pointer items-start gap-3 rounded-md border border-border p-3 text-[13px]">
            <input
              type="radio"
              name="master-mode"
              value="set-new"
              checked={mode === "set-new"}
              onChange={() => setMode("set-new")}
              aria-label="Set a different master password"
              className="mt-0.5"
            />
            <div className="flex-1">
              <div className="font-medium text-text">
                Set a different master password
              </div>
              <div className="text-text-soft">
                We'll re-encrypt every synced vault to the new master. Needs
                network access to each.
              </div>
            </div>
          </label>
        </fieldset>

        {mode === "set-new" && (
          <>
            <div>
              <label
                htmlFor="import-new-master"
                className="mb-1 block text-[11px] uppercase tracking-[0.1em] text-text-dim"
              >
                New master
              </label>
              <PassphraseInput
                id="import-new-master"
                autoCompleteMode="new-password"
                aria-label="New master"
                value={newPass}
                onChange={(e) => setNewPass(e.target.value)}
              />
              <PassphraseStrength passphrase={newPass} />
              {!strong && newPass.length > 0 && (
                <div className="mt-1.5 text-[12px] text-danger">
                  Too weak — use 5+ random words.
                </div>
              )}
            </div>
            <div>
              <label
                htmlFor="import-confirm-master"
                className="mb-1 block text-[11px] uppercase tracking-[0.1em] text-text-dim"
              >
                Confirm new master
              </label>
              <PassphraseInput
                id="import-confirm-master"
                autoCompleteMode="new-password"
                aria-label="Confirm new master"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
              />
              {!matches && confirm.length > 0 && (
                <div className="mt-1.5 text-[12px] text-danger">
                  Confirmation does not match.
                </div>
              )}
            </div>
          </>
        )}

        <div className="mt-6 flex flex-col-reverse gap-2 md:flex-row md:justify-between">
          <button
            type="button"
            onClick={onBack}
            className="text-center text-[12px] text-text-dim underline hover:text-text md:py-2"
          >
            ← Back
          </button>
          <button
            type="submit"
            disabled={!canContinue}
            className="rounded-md bg-accent px-4 py-3 text-sm font-medium text-white disabled:opacity-40 md:py-2"
          >
            Create vault →
          </button>
        </div>
      </form>
    </div>
  );
}

// ————— Step 3: create + (optionally) rotate ——————————————

interface CreateStepProps {
  readonly decrypted: DecryptedBackup;
  readonly chosenMaster: string;
  readonly mode: MasterMode;
  readonly createError: string | null;
  readonly rotationProgress: RotationProgress | null;
  readonly worker: WorkerProxy;
  readonly setMaster: (m: {
    masterHandle: number;
    appConfig: AppConfig;
    unlockedAt: number;
  }) => void;
  readonly setSession: (s: {
    handle: number;
    vaultId: string;
    displayName: string;
    unlockedAt: number;
  }) => void;
  readonly navigate: (path: string) => void;
  readonly onError: (msg: string | null) => void;
  readonly onRotationProgress: (p: RotationProgress | null) => void;
  readonly onStartOver: () => void;
  readonly startedRef: React.MutableRefObject<boolean>;
}

function CreateStep({
  decrypted,
  chosenMaster,
  mode,
  createError,
  rotationProgress,
  worker,
  setMaster,
  setSession,
  navigate,
  onError,
  onRotationProgress,
  onStartOver,
  startedRef,
}: CreateStepProps) {
  // Run-once on mount. The ref guard survives React Strict Mode's
  // double-invocation (the second invocation reuses the same ref
  // object) so the on-disk pipeline only fires once. Mirrors
  // `MasterSetupWizard`'s `Step4Create` pattern.
  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    void runImport({
      decrypted,
      chosenMaster,
      mode,
      worker,
      setMaster,
      setSession,
      navigate,
      onError,
      onRotationProgress,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="mx-auto min-h-[70vh] max-w-md px-5 py-8 pb-[calc(env(safe-area-inset-bottom)+24px)] md:px-6 md:py-12">
      {createError === null ? (
        <div className="flex flex-col items-center justify-center py-10 text-center">
          <div className="mb-3 h-5 w-5 animate-spin rounded-full border-2 border-accent border-t-transparent" />
          <div className="text-sm text-text-soft">Importing your vault…</div>
        </div>
      ) : (
        <div className="rounded-lg border border-danger/40 bg-danger/10 p-4">
          <div className="mb-2 text-sm font-semibold text-danger">
            Import failed
          </div>
          <p className="mb-3 text-[13px] leading-relaxed text-text">
            {createError}
          </p>
          <button
            type="button"
            onClick={onStartOver}
            className="text-[13px] text-accent-soft underline hover:text-text"
          >
            ← Start over
          </button>
        </div>
      )}

      {rotationProgress !== null && (
        <MasterRotationModal
          progress={rotationProgress}
          onClose={() => onRotationProgress(null)}
        />
      )}
    </div>
  );
}

interface RunImportArgs {
  readonly decrypted: DecryptedBackup;
  readonly chosenMaster: string;
  readonly mode: MasterMode;
  readonly worker: WorkerProxy;
  readonly setMaster: CreateStepProps["setMaster"];
  readonly setSession: CreateStepProps["setSession"];
  readonly navigate: CreateStepProps["navigate"];
  readonly onError: CreateStepProps["onError"];
  readonly onRotationProgress: CreateStepProps["onRotationProgress"];
}

async function runImport(args: RunImportArgs): Promise<void> {
  const {
    decrypted,
    chosenMaster,
    mode,
    worker,
    setMaster,
    setSession,
    navigate,
    onError,
    onRotationProgress,
  } = args;

  const masterPassword =
    mode === "use-backup" ? decrypted.password : chosenMaster;
  const rotateSynced = mode === "set-new";
  const newOpfsId = newOpfsVaultId();
  const importedConfig = buildImportedAppConfig({
    backup: decrypted.config,
    newOpfsId,
    opfsSubdir: OPFS_SUBDIR,
    rotateSynced,
    nowSeconds: Math.floor(Date.now() / 1000),
  });

  const masterBytes = toPassphraseBytes(masterPassword);
  let handle: number | null = null;
  try {
    handle = await worker.createOpfsVault(
      OPFS_SUBDIR,
      masterBytes,
      "primary",
      false,
    );
    await worker.saveAppConfig(handle, importedConfig);

    const unlockedAt = Date.now();
    setMaster({
      masterHandle: handle,
      appConfig: importedConfig,
      unlockedAt,
    });
    setSession({
      handle,
      vaultId: newOpfsId,
      displayName: "primary",
      unlockedAt,
    });

    if (rotateSynced) {
      // The synced S3 vaults in `importedConfig` carry pending markers
      // (set by `buildImportedAppConfig`). `retryPendingRotations`
      // reads `useMasterSession.getState()` which we just populated
      // above, and rewrites the config without those markers when
      // each vault rotates. `WorkerSubset` accepts the
      // `Remote<VaultWorkerApi>` shape directly so no cast is
      // needed.
      const workerSubset: WorkerSubset = worker;
      await retryPendingRotations(
        workerSubset,
        decrypted.password,
        chosenMaster,
        { onProgress: onRotationProgress },
      );
    }

    navigate(`/v/${newOpfsId}/~/`);
  } catch (err) {
    onError(err instanceof Error ? err.message : String(err));
    if (handle !== null) {
      // Best-effort rollback of the worker handle. The on-disk OPFS
      // vault may have been written; the user's recovery is "Start
      // over" → "Wipe app" on the unlock screen, same path as
      // MasterSetupWizard's recovery.
      await worker.lockVault(handle).catch(() => {
        /* best-effort */
      });
      // If we already populated the master + session stores (we do
      // that BEFORE running the rotation step so the rotation
      // orchestrator can read `useMasterSession.getState()`), the
      // stores now point at a locked, unusable handle. Clear them
      // so the user lands on the unlock screen on next render
      // instead of staring at a dead session.
      useMasterSession.getState().clear();
      useSession.getState().clear();
    }
  } finally {
    zeroize(masterBytes);
    // Drop the module-scoped chosen-master string the moment the
    // pipeline is done with it (success OR failure). `reset()` also
    // clears the stash, but a successful import navigates away
    // without ever calling reset — without this, the password
    // would persist for the lifetime of the JS module.
    createRefStash.master = null;
    createRefStash.mode = "use-backup";
  }
}
